import React, { useEffect, useRef, useState } from 'react';
import { fabric } from 'fabric';
import {
    MousePointer, PenTool, Type, Square, Circle,
    ArrowRight, Eraser, Download, Check, X,
    Undo, Trash2, Highlighter, Pencil, Crop, Hand, Minus,
    Bold, Italic, Underline
} from 'lucide-react';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { ChromePicker } from 'react-color';
import { toast } from 'react-hot-toast';
import './ImageAnnotator.css';

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 10;
const ZOOM_FACTOR = 0.999;

const ImageAnnotator = ({ image, onSave, onCancel }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [fabricCanvas, setFabricCanvas] = useState(null);
    const [activeTool, setActiveTool] = useState('pan'); // pan, select, pencil, pen, highlighter, text, rect, circle, arrow, crop
    const [strokeColor, setStrokeColor] = useState('#ff0000');
    const [fillColor, setFillColor] = useState('#ff0000');
    const [strokeWidth, setStrokeWidth] = useState(3);
    const [fillEnabled, setFillEnabled] = useState(false);
    const [showColorPicker, setShowColorPicker] = useState(null); // 'stroke' | 'fill' | null
    const [history, setHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [isInternalUpdate, setIsInternalUpdate] = useState(false);

    // Crop logic
    const [currentImage, setCurrentImage] = useState(image);
    const [isCropMode, setIsCropMode] = useState(false);
    const cropperRef = useRef(null);
    const [aspectRatio, setAspectRatio] = useState(NaN);

    const activeToolRef = useRef(activeTool);
    useEffect(() => {
        activeToolRef.current = activeTool;
    }, [activeTool]);

    const arrowStartPointRef = useRef(null);
    const setActiveToolRef = useRef(() => {});
    const strokeColorRef = useRef(strokeColor);
    const strokeWidthRef = useRef(strokeWidth);
    useEffect(() => {
        setActiveToolRef.current = setActiveTool;
        strokeColorRef.current = strokeColor;
        strokeWidthRef.current = strokeWidth;
    }, [setActiveTool, strokeColor, strokeWidth]);

    const [selectedObjectType, setSelectedObjectType] = useState(null);
    const [textToolbarVersion, setTextToolbarVersion] = useState(0);

    const colors = ['#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50', '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800', '#ff5722', '#795548', '#9e9e9e', '#607d8b', '#000000', '#ffffff'];

    useEffect(() => {
        if (!containerRef.current || !canvasRef.current || !image) return;

        const container = containerRef.current;
        const initialWidth = container.clientWidth;
        const initialHeight = container.clientHeight;

        const canvas = new fabric.Canvas(canvasRef.current, {
            width: initialWidth,
            height: initialHeight,
            backgroundColor: '#1a1a1a',
            selection: true
        });

        setFabricCanvas(canvas);

        let bgImage = null;

        // Function to save history
        const saveHistory = () => {
            if (isInternalUpdate) return;
            const json = canvas.toJSON();
            setHistory(prev => {
                const newHistory = prev.slice(0, historyIndex + 1);
                return [...newHistory, json];
            });
            setHistoryIndex(prev => prev + 1);
        };

        canvas.on('object:added', (e) => {
            if (e.target && !e.target.isHistoryAction) {
                saveHistory();
            }
        });
        canvas.on('object:modified', saveHistory);
        canvas.on('object:removed', (e) => {
            if (e.target && !e.target.isHistoryAction) {
                saveHistory();
            }
        });

        const handleWheel = (opt) => {
            const delta = opt.e.deltaY;
            let zoom = canvas.getZoom();
            zoom *= ZOOM_FACTOR ** delta;
            zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom));
            const point = canvas.getPointer(opt.e);
            canvas.zoomToPoint(point, zoom);
            opt.e.preventDefault();
            opt.e.stopPropagation();
            canvas.requestRenderAll();
        };
        canvas.on('mouse:wheel', handleWheel);

        let isPanning = false;
        const handleMouseDown = (opt) => {
            if (activeToolRef.current === 'arrow') {
                opt.e?.preventDefault?.();
                const pt = canvas.getPointer(opt.e);
                const start = arrowStartPointRef.current;
                if (!start) {
                    arrowStartPointRef.current = { x: pt.x, y: pt.y };
                    return;
                }
                const x1 = start.x, y1 = start.y, x2 = pt.x, y2 = pt.y;
                const dx = x2 - x1, dy = y2 - y1;
                const len = Math.sqrt(dx * dx + dy * dy);
                if (len < 2) {
                    arrowStartPointRef.current = null;
                    setActiveToolRef.current?.('select');
                    return;
                }
                const ux = dx / len, uy = dy / len;
                const arrowLen = 8;
                const halfW = 4;
                const lineEndX = x2 - ux * arrowLen, lineEndY = y2 - uy * arrowLen;
                const stroke = strokeColorRef.current ?? '#ff0000';
                const strokeW = strokeWidthRef.current ?? 3;
                const line = new fabric.Line([x1, y1, lineEndX, lineEndY], {
                    stroke,
                    strokeWidth: strokeW,
                    selectable: true,
                    evented: true
                });
                const tipX = x2, tipY = y2;
                const baseX = lineEndX, baseY = lineEndY;
                const perpX = -uy * halfW, perpY = ux * halfW;
                const pathStr = `M ${tipX} ${tipY} L ${baseX + perpX} ${baseY + perpY} L ${baseX - perpX} ${baseY - perpY} Z`;
                const head = new fabric.Path(pathStr, {
                    fill: stroke,
                    stroke: stroke,
                    strokeWidth: strokeW,
                    selectable: true,
                    evented: true
                });
                const arrowGroup = new fabric.Group([line, head], {
                    selectable: true,
                    evented: true,
                    hasControls: true,
                    hasBorders: true,
                    cornerColor: 'white',
                    cornerStrokeColor: '#2563eb',
                    cornerSize: 12,
                    cornerStyle: 'circle',
                    transparentCorners: false,
                    padding: 6,
                    lockScalingX: false,
                    lockScalingY: false
                });
                canvas.add(arrowGroup);
                canvas.setActiveObject(arrowGroup);
                saveHistory();
                arrowStartPointRef.current = null;
                setActiveToolRef.current?.('select');
                canvas.requestRenderAll();
                return;
            }
            if (activeToolRef.current === 'pan') isPanning = true;
        };
        const handleMouseMove = (opt) => {
            if (isPanning && activeToolRef.current === 'pan' && opt.e) {
                canvas.relativePan(new fabric.Point(opt.e.movementX, opt.e.movementY));
                canvas.requestRenderAll();
            }
        };
        const handleMouseUp = () => {
            isPanning = false;
        };
        canvas.on('mouse:down', handleMouseDown);
        canvas.on('mouse:move', handleMouseMove);
        canvas.on('mouse:up', handleMouseUp);

        // Function to fit image to canvas
        const fitImage = (img, cWidth, cHeight) => {
            const scaleX = cWidth / img.width;
            const scaleY = cHeight / img.height;
            const scale = Math.min(scaleX, scaleY) * 0.9; // 90% fit

            img.set({
                scaleX: scale,
                scaleY: scale,
                left: cWidth / 2,
                top: cHeight / 2,
                originX: 'center',
                originY: 'center'
            });
        };

        // Load image
        fabric.Image.fromURL(currentImage, (img) => {
            if (!img) return;
            bgImage = img;
            fitImage(img, initialWidth, initialHeight);
            img.set({
                selectable: false,
                evented: false,
                name: 'background'
            });
            canvas.add(img);
            canvas.sendToBack(img);
            canvas.renderAll();

            // Initial history state if history is empty
            if (history.length === 0) {
                const json = canvas.toJSON();
                setHistory([json]);
                setHistoryIndex(0);
            }
        }, { crossOrigin: 'anonymous' });

        // Observe resize
        const resizeObserver = new ResizeObserver((entries) => {
            if (!entries.length) return;
            const { width, height } = entries[0].contentRect;

            canvas.setDimensions({ width, height });
            canvas.setZoom(1);
            canvas.viewportTransform[4] = 0;
            canvas.viewportTransform[5] = 0;
            if (bgImage) {
                fitImage(bgImage, width, height);
            }
            canvas.renderAll();
        });

        resizeObserver.observe(container);

        return () => {
            canvas.off('mouse:wheel', handleWheel);
            canvas.off('mouse:down', handleMouseDown);
            canvas.off('mouse:move', handleMouseMove);
            canvas.off('mouse:up', handleMouseUp);
            resizeObserver.disconnect();
            canvas.dispose();
        };
    }, [currentImage]);

    useEffect(() => {
        if (!fabricCanvas) return;

        if (activeTool === 'pan') {
            fabricCanvas.selection = false;
            fabricCanvas.discardActiveObject();
        } else if (activeTool === 'select') {
            fabricCanvas.selection = true;
        }

        const isDrawingTool = ['pencil', 'pen', 'highlighter'].includes(activeTool);
        fabricCanvas.isDrawingMode = isDrawingTool;

        if (isDrawingTool) {
            let brushOpacity = 1;
            if (activeTool === 'highlighter') {
                brushOpacity = 0.4;
            }

            fabricCanvas.freeDrawingBrush = new fabric.PencilBrush(fabricCanvas);
            fabricCanvas.freeDrawingBrush.width = strokeWidth;

            // Hex to RGBA conversion for highlighter
            const r = parseInt(strokeColor.slice(1, 3), 16);
            const g = parseInt(strokeColor.slice(3, 5), 16);
            const b = parseInt(strokeColor.slice(5, 7), 16);

            if (activeTool === 'highlighter') {
                fabricCanvas.freeDrawingBrush.color = `rgba(${r}, ${g}, ${b}, ${brushOpacity})`;
            } else {
                fabricCanvas.freeDrawingBrush.color = strokeColor;
            }

            fabricCanvas.discardActiveObject();
            fabricCanvas.requestRenderAll();
        }

        // Update selected object if properties change
        const activeObject = fabricCanvas.getActiveObject();
        if (activeObject && activeObject.type !== 'image') {
            if (activeObject.type === 'i-text') {
                activeObject.set('fill', strokeColor);
            } else {
                activeObject.set('stroke', strokeColor);
                activeObject.set('strokeWidth', strokeWidth);
                if (activeObject.type === 'rect' || activeObject.type === 'circle') {
                    activeObject.set('fill', fillEnabled ? fillColor : 'transparent');
                }
            }
            fabricCanvas.renderAll();
        }

    }, [fabricCanvas, activeTool, strokeColor, fillColor, strokeWidth, fillEnabled]);

    useEffect(() => {
        if (!fabricCanvas) return;
        const updateSelection = () => {
            const active = fabricCanvas.getActiveObject();
            setSelectedObjectType(active?.type ?? null);
        };
        fabricCanvas.on('selection:created', updateSelection);
        fabricCanvas.on('selection:updated', updateSelection);
        fabricCanvas.on('selection:cleared', updateSelection);
        return () => {
            fabricCanvas.off('selection:created', updateSelection);
            fabricCanvas.off('selection:updated', updateSelection);
            fabricCanvas.off('selection:cleared', updateSelection);
        };
    }, [fabricCanvas]);

    const applyTextStyle = (key, value) => {
        const active = fabricCanvas?.getActiveObject();
        if (active?.type !== 'i-text') return;
        active.set(key, value);
        fabricCanvas.requestRenderAll();
        setTextToolbarVersion((v) => v + 1);
    };

    const hexToRgba = (hex, opacity) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    };

    const addShape = (type) => {
        if (!fabricCanvas) return;

        const centerL = fabricCanvas.width / 2;
        const centerT = fabricCanvas.height / 2;
        let object;

        const commonProps = {
            left: centerL,
            top: centerT,
            fill: 'transparent',
            stroke: strokeColor,
            strokeWidth: strokeWidth,
            originX: 'center',
            originY: 'center',
            cornerColor: 'white',
            cornerStrokeColor: '#2563eb',
            cornerSize: 12,
            cornerStyle: 'circle',
            transparentCorners: false,
            padding: 6,
            lockScalingX: false,
            lockScalingY: false
        };

        if (type === 'rect') {
            object = new fabric.Rect({
                ...commonProps,
                width: 100,
                height: 100,
                fill: fillEnabled ? fillColor : 'transparent',
            });
        } else if (type === 'circle') {
            object = new fabric.Circle({
                ...commonProps,
                radius: 50,
                fill: fillEnabled ? fillColor : 'transparent',
            });
        } else if (type === 'text') {
            const textContainer = containerRef.current ?? undefined;
            object = new fabric.IText('Texto', {
                ...commonProps,
                fill: strokeColor,
                stroke: null,
                fontSize: 24,
                fontFamily: 'Arial',
                editable: true,
                fontWeight: 'normal',
                fontStyle: 'normal',
                underline: false,
                hiddenTextareaContainer: textContainer
            });
        } else if (type === 'arrow') {
            arrowStartPointRef.current = null;
            setActiveTool('arrow');
            return;
        } else if (type === 'line') {
            object = new fabric.Line([centerL - 50, centerT, centerL + 50, centerT], {
                ...commonProps,
                stroke: strokeColor,
                strokeWidth: strokeWidth,
            });
        }

        if (object) {
            fabricCanvas.add(object);
            fabricCanvas.setActiveObject(object);
            setActiveTool('select');
            if (type === 'text' && object.enterEditing) {
                setTimeout(() => {
                    object.enterEditing();
                    object.hiddenTextarea?.focus?.();
                }, 0);
            }
            fabricCanvas.requestRenderAll();
        }
    };

    const handleUndo = () => {
        if (historyIndex > 0 && fabricCanvas) {
            setIsInternalUpdate(true);
            const prevIndex = historyIndex - 1;
            const state = history[prevIndex];

            fabricCanvas.loadFromJSON(state, () => {
                // Mark background image
                const objects = fabricCanvas.getObjects();
                if (objects[0] && objects[0].type === 'image') {
                    objects[0].set({ selectable: false, evented: false });
                }
                fabricCanvas.renderAll();
                setHistoryIndex(prevIndex);
                setIsInternalUpdate(false);
            });
        }
    };

    const handleRedo = () => {
        if (historyIndex < history.length - 1 && fabricCanvas) {
            setIsInternalUpdate(true);
            const nextIndex = historyIndex + 1;
            const state = history[nextIndex];

            fabricCanvas.loadFromJSON(state, () => {
                const objects = fabricCanvas.getObjects();
                if (objects[0] && objects[0].type === 'image') {
                    objects[0].set({ selectable: false, evented: false });
                }
                fabricCanvas.renderAll();
                setHistoryIndex(nextIndex);
                setIsInternalUpdate(false);
            });
        }
    };

    const handleConfirmCrop = () => {
        if (!cropperRef.current) return;

        const canvas = cropperRef.current.getCroppedCanvas();
        if (!canvas) {
            toast.error("Não foi possível gerar a imagem recortada.");
            return;
        }

        const croppedImage = canvas.toDataURL('image/jpeg', 0.9);
        setCurrentImage(croppedImage);
        setIsCropMode(false);

        // When image changes, the useEffect will recreate the canvas with new BG
        // Resetting history for the new base image
        setHistory([]);
        setHistoryIndex(-1);

        toast.success("Imagem recortada!");
    };

    const handleSave = () => {
        if (!fabricCanvas) return;

        // Export the actual container content
        const dataURL = fabricCanvas.toDataURL({
            format: 'jpeg',
            quality: 0.9,
            multiplier: 2 // Higher res export
        });

        // Convert to blob
        fetch(dataURL)
            .then(res => res.blob())
            .then(blob => onSave(blob));
    };

    const deleteActive = () => {
        const active = fabricCanvas?.getActiveObjects();
        if (active && active.length) {
            fabricCanvas.discardActiveObject();
            active.forEach(obj => fabricCanvas.remove(obj));
            fabricCanvas.requestRenderAll();
        }
    };

    const clearAll = () => {
        if (confirm('Limpar todas as edições?')) {
            // Remove all except the background image (index 0)
            const objects = fabricCanvas.getObjects();
            // Assuming image is first
            for (let i = objects.length - 1; i > 0; i--) {
                fabricCanvas.remove(objects[i]);
            }
        }
    };

    return (
        <div className="image-annotator">
            <div className="annotator-toolbar-left">
                <div className="tool-group">
                    <span className="tool-label">Desenho</span>
                    <div className="tool-grid-3">
                        <button className={`tool-btn-small ${activeTool === 'pencil' ? 'active' : ''}`} onClick={() => { setActiveTool('pencil'); setStrokeWidth(2); }} title="Lápis">
                            <Pencil size={18} /> <span>Lápis</span>
                        </button>
                        <button className={`tool-btn-small ${activeTool === 'pen' ? 'active' : ''}`} onClick={() => { setActiveTool('pen'); setStrokeWidth(6); }} title="Caneta">
                            <PenTool size={18} /> <span>Caneta</span>
                        </button>
                        <button className={`tool-btn-small ${activeTool === 'highlighter' ? 'active' : ''}`} onClick={() => { setActiveTool('highlighter'); setStrokeWidth(20); }} title="Marca-texto">
                            <Highlighter size={18} /> <span>Marca</span>
                        </button>
                    </div>
                </div>

                <div className="tool-group">
                    <span className="tool-label">Formas</span>
                    <div className="tool-grid">
                        <button className={`tool-btn-small`} onClick={() => addShape('rect')} title="Retângulo">
                            <Square size={18} /> <span>Caixa</span>
                        </button>
                        <button className={`tool-btn-small`} onClick={() => addShape('circle')} title="Círculo">
                            <Circle size={18} /> <span>Círculo</span>
                        </button>
                        <button className={`tool-btn-small ${activeTool === 'arrow' ? 'active' : ''}`} onClick={() => addShape('arrow')} title="Seta (clique no canvas para início, depois para o fim)">
                            <ArrowRight size={18} /> <span>Seta</span>
                        </button>
                        <button className={`tool-btn-small`} onClick={() => addShape('line')} title="Linha">
                            <Minus size={18} /> <span>Linha</span>
                        </button>
                        <button className={`tool-btn-small`} onClick={() => addShape('text')} title="Adicionar Texto">
                            <Type size={18} /> <span>Texto</span>
                        </button>
                    </div>
                </div>

                {selectedObjectType === 'i-text' && fabricCanvas && (() => {
                    const obj = fabricCanvas.getActiveObject();
                    if (!obj || obj.type !== 'i-text') return null;
                    return (
                        <div className="tool-group" key={`text-toolbar-${textToolbarVersion}`}>
                            <span className="tool-label">Formato do texto</span>
                            <div className="style-section">
                                <span className="style-label-small">Tamanho</span>
                                <input
                                    type="number"
                                    min={12}
                                    max={72}
                                    className="text-font-size-input"
                                    value={obj.fontSize || 24}
                                    onChange={(e) => applyTextStyle('fontSize', parseInt(e.target.value, 10) || 24)}
                                />
                            </div>
                            <div className="text-format-buttons">
                                <button
                                    type="button"
                                    className={`tool-btn icon-only ${obj.fontWeight === 'bold' ? 'active' : ''}`}
                                    onClick={() => applyTextStyle('fontWeight', obj.fontWeight === 'bold' ? 'normal' : 'bold')}
                                    title="Negrito"
                                >
                                    <Bold size={18} />
                                </button>
                                <button
                                    type="button"
                                    className={`tool-btn icon-only ${obj.fontStyle === 'italic' ? 'active' : ''}`}
                                    onClick={() => applyTextStyle('fontStyle', obj.fontStyle === 'italic' ? 'normal' : 'italic')}
                                    title="Itálico"
                                >
                                    <Italic size={18} />
                                </button>
                                <button
                                    type="button"
                                    className={`tool-btn icon-only ${obj.underline ? 'active' : ''}`}
                                    onClick={() => applyTextStyle('underline', !obj.underline)}
                                    title="Sublinhado"
                                >
                                    <Underline size={18} />
                                </button>
                            </div>
                        </div>
                    );
                })()}

                <div className="tool-group">
                    <span className="tool-label">Estilo</span>

                    <div className="style-section">
                        <span className="style-label-small">Contorno / Linha</span>
                        <div className="color-control-wrapper">
                            <button
                                className="color-preview-btn"
                                style={{ backgroundColor: strokeColor }}
                                onClick={() => setShowColorPicker(showColorPicker === 'stroke' ? null : 'stroke')}
                            />
                            {showColorPicker === 'stroke' && (
                                <div className="color-picker-popover">
                                    <div className="color-picker-cover" onClick={() => setShowColorPicker(null)} />
                                    <div className="color-picker-content">
                                        <div className="color-grid-mini">
                                            {['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ffffff', '#000000', '#ff00ff', '#00ffff', '#ffa500'].map(c => (
                                                <button
                                                    key={c}
                                                    className={`color-dot ${strokeColor === c ? 'active' : ''}`}
                                                    style={{ backgroundColor: c }}
                                                    onClick={() => setStrokeColor(c)}
                                                />
                                            ))}
                                        </div>
                                        <ChromePicker color={strokeColor} onChange={(c) => setStrokeColor(c.hex)} />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="stroke-control">
                            <input
                                type="range" min="1" max="30"
                                value={strokeWidth}
                                onChange={(e) => setStrokeWidth(parseInt(e.target.value))}
                            />
                            <span className="value-tag">{strokeWidth}px</span>
                        </div>
                    </div>

                    <div className="style-section">
                        <label className="fill-toggle">
                            <input type="checkbox" checked={fillEnabled} onChange={(e) => setFillEnabled(e.target.checked)} />
                            <span>Habilitar Preenchimento</span>
                        </label>
                        {fillEnabled && (
                            <div className="color-control-wrapper">
                                <button
                                    className="color-preview-btn"
                                    style={{ backgroundColor: fillColor }}
                                    onClick={() => setShowColorPicker(showColorPicker === 'fill' ? null : 'fill')}
                                />
                                {showColorPicker === 'fill' && (
                                    <div className="color-picker-popover">
                                        <div className="color-picker-cover" onClick={() => setShowColorPicker(null)} />
                                        <div className="color-picker-content">
                                            <div className="color-grid-mini">
                                                {['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ffffff', '#000000', '#ff00ff', '#00ffff', '#ffa500'].map(c => (
                                                    <button
                                                        key={c}
                                                        className={`color-dot ${fillColor === c ? 'active' : ''}`}
                                                        style={{ backgroundColor: c }}
                                                        onClick={() => setFillColor(c)}
                                                    />
                                                ))}
                                            </div>
                                            <ChromePicker color={fillColor} onChange={(c) => setFillColor(c.hex)} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="annotator-workspace" ref={containerRef}>
                <canvas ref={canvasRef} style={{ display: isCropMode ? 'none' : 'block' }} />

                {isCropMode && (
                    <div className="integrated-cropper-container">
                        <Cropper
                            src={currentImage}
                            style={{ height: '100%', width: '100%' }}
                            initialAspectRatio={NaN}
                            aspectRatio={aspectRatio}
                            guides={true}
                            viewMode={1}
                            dragMode="move"
                            responsive={true}
                            background={true}
                            autoCropArea={1}
                            onInitialized={(instance) => {
                                cropperRef.current = instance;
                            }}
                        />
                        <div className="cropper-overlay-actions">
                            <select
                                className="aspect-select-mini"
                                value={Number.isNaN(aspectRatio) ? 'NaN' : aspectRatio}
                                onChange={(e) => {
                                    const val = e.target.value === 'NaN' ? NaN : parseFloat(e.target.value);
                                    setAspectRatio(val);
                                    cropperRef.current?.setAspectRatio(val);
                                }}
                            >
                                <option value="NaN">Livre</option>
                                <option value="1">1:1 (Quadrado)</option>
                                <option value="1.7777777777777777">16:9 (Widescreen)</option>
                                <option value="1.3333333333333333">4:3 (Padrão)</option>
                            </select>
                            <button className="tool-btn small confirm" onClick={handleConfirmCrop}><Check size={16} /> Confirmar</button>
                            <button className="tool-btn small warning" onClick={() => setIsCropMode(false)}><X size={16} /> Cancelar</button>
                        </div>
                    </div>
                )}
            </div>

            <div className="annotator-toolbar-right">
                <div className="tool-group">
                    <button className={`tool-btn icon-only ${activeTool === 'pan' ? 'active' : ''}`} onClick={() => setActiveTool('pan')} title="Mover">
                        <Hand size={24} />
                    </button>
                    <button className={`tool-btn icon-only ${activeTool === 'select' ? 'active' : ''}`} onClick={() => setActiveTool('select')} title="Selecionar">
                        <MousePointer size={24} />
                    </button>
                    <button className={`tool-btn icon-only ${isCropMode ? 'active' : ''}`} onClick={() => setIsCropMode(true)} title="Recortar">
                        <Crop size={24} />
                    </button>
                    <button className="tool-btn icon-only" onClick={handleUndo} disabled={historyIndex <= 0} title="Desfazer">
                        <Undo size={24} />
                    </button>
                    <button className="tool-btn icon-only" onClick={handleRedo} disabled={historyIndex >= history.length - 1} title="Refazer">
                        <Undo size={24} style={{ transform: 'scaleX(-1)' }} />
                    </button>
                    <button className="tool-btn icon-only warning" onClick={deleteActive} title="Apagar Selecionado">
                        <Trash2 size={24} />
                    </button>
                    <button className="tool-btn icon-only warning" onClick={clearAll} title="Limpar Tudo">
                        <Eraser size={24} />
                    </button>
                </div>
                <div className="tool-group spacer"></div>
                <button className="action-btn confirm" onClick={handleSave}>
                    <Check size={24} />
                    <span>Salvar</span>
                </button>
            </div>
        </div>
    );
};

export default ImageAnnotator;
